"use client";

import { ArrowRightLeft } from "lucide-react";
import { formatDate } from "date-fns";
import React from "react";

interface RouteInfo {
  fromCityOrAirport: { code: string };
  toCityOrAirport: { code: string };
  travelDate: string;
}

interface PaxInfo {
  ADULT?: string | number;
  CHILD?: string | number;
  INFANT?: string | number;
}

interface PublicShareJourneyHeaderProps {
  searchQuery: {
    routeInfos?: RouteInfo[];
    paxInfo?: PaxInfo;
    cabinClass?: string;
  };
  metadata: Array<{
    fromCityOrAirport?: string;
    toCityOrAirport?: string;
    travelDate?: string;
  }>;
  tripType: "oneway" | "roundtrip" | "multicity" | string;
}

const PublicShareJourneyHeader: React.FC<PublicShareJourneyHeaderProps> = ({
  searchQuery,
  metadata,
  tripType,
}) => {
  const routeInfos = searchQuery?.routeInfos ?? [];
  const paxInfo = searchQuery?.paxInfo ?? { ADULT: 1, CHILD: 0, INFANT: 0 };

  if (!routeInfos.length) {
    return null;
  }

  return (
    <div className="bg-[#333] w-full">
      <div className="container mx-auto py-4 md:py-2 px-3">
        <div className="flex gap-y-4 lg:gap-y-2 flex-wrap">
          <ul
            className={`flex ${
              tripType === "multicity"
                ? "w-full md:w-[46%] overflow-x-auto"
                : "w-full sm:w-[170px] lg:w-[230px] xl:w-[280px]"
            }`}
          >
            {tripType === "multicity" ? (
              routeInfos.map((route, index) => (
                <li
                  key={index}
                  className="w-full lg:max-w-[300px] mb-4 lg:mb-0"
                >
                  <ul className="flex justify-start items-center w-full relative text-white lg:after:absolute lg:after:content-[''] lg:after:w-px lg:after:h-[65%] lg:after:right-6 lg:after:top-[7px] lg:after:bg-[#999] text-[.725rem] lg:text-[13px]">
                    <li className="inline-block font-bold align-middle mr-2">
                      {route.fromCityOrAirport.code}
                      <br />
                      <span className="font-normal text-[#ccc] text-[.725rem] lg:text-[13px]">
                        {metadata[index]?.fromCityOrAirport}
                      </span>
                    </li>
                    <li className="mr-2 text-[22px] text-[#ccc]">✈︎</li>
                    <li className="inline-block font-bold align-middle mr-5">
                      {route.toCityOrAirport.code}
                      <br />
                      <span className="font-normal text-[#ccc] text-[.725rem] lg:text-[13px]">
                        {metadata[index]?.toCityOrAirport}
                      </span>
                    </li>
                  </ul>
                </li>
              ))
            ) : (
              <li className="w-full lg:mb-0">
                <ul className="flex sm:justify-start items-center w-full relative text-white lg:after:absolute lg:after:content-[''] lg:after:w-px lg:after:h-[65%] lg:after:right-6 lg:after:top-[7px] lg:after:bg-[#999] text-[.725rem] lg:text-[13px]">
                  <li className="inline-block font-bold align-middle mr-5">
                    {routeInfos[0].fromCityOrAirport.code}
                    <br />
                    <span className="font-normal text-[#ccc] text-[.725rem] lg:text-[13px]">
                      {metadata[0]?.fromCityOrAirport}
                    </span>
                  </li>
                  <li className="mr-5 text-[22px] text-[#ccc]">
                    {tripType === "roundtrip" ? (
                      <ArrowRightLeft className="w-6 h-6" />
                    ) : (
                      "✈︎ "
                    )}
                  </li>
                  <li className="inline-block font-bold align-middle mr-5">
                    {routeInfos[0].toCityOrAirport.code}
                    <br />
                    <span className="font-normal text-[#ccc] text-[.725rem] lg:text-[13px]">
                      {metadata[0]?.toCityOrAirport}
                    </span>
                  </li>
                </ul>
              </li>
            )}
          </ul>

          {tripType !== "multicity" && routeInfos[0]?.travelDate && (
            <div className="relative w-full sm:w-[170px] lg:after:absolute after:content-[''] lg:after:w-px lg:after:h-[65%] lg:after:right-5 lg:after:top-[7px] lg:after:bg-[#999]">
              <p className="text-sm font-medium text-[#ccc]">Departure Date</p>
              <span className="text-[11px] font-normal text-white">
                {formatDate(routeInfos[0].travelDate, "dd MMM yyyy")}
              </span>
            </div>
          )}

          {tripType === "roundtrip" && routeInfos[1]?.travelDate && (
            <div className="relative w-full sm:w-[170px] lg:after:absolute lg:after:content-[''] lg:after:w-px after:h-[65%] lg:after:right-5 lg:after:top-[7px] lg:after:bg-[#999]">
              <p className="text-sm font-medium text-[#ccc]">Return Date</p>
              <span className="text-[11px] font-normal text-white">
                {formatDate(routeInfos[1].travelDate, "dd MMM yyyy")}
              </span>
            </div>
          )}

          <div className="relative w-full sm:w-[200px]">
            <p className="text-sm font-medium text-[#ccc]">
              Passengers &amp; Class
            </p>
            <span className="text-[11px] font-normal text-white">
              {`${paxInfo.ADULT ?? 0} ADULT ${paxInfo.CHILD ?? 0} CHILD ${paxInfo.INFANT ?? 0} INFANT`}{" "}
              | {searchQuery.cabinClass ?? "ECONOMY"}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicShareJourneyHeader;
